/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { formatCurrency, getErrorMessage } from '@sportninja/common/utils/utils'
import {
  getFeeLineItems,
  splitFeeLineItemsForReceipt,
} from '../utils/feeLineItems'
import { Alert, Spin } from 'antd'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { TEAM_BUDGET_PAYMENT_COPY } from '@sportninja/common/features/registration/teamBudgetPayment/constants/copy'
import { isConfirmedRegistrationSession } from '@sportninja/common/features/registration/teamBudgetPayment/utils/confirmedSession'
import {
  goesToTeamBudgetPaymentStep,
  offersTeamBudgetPayment,
  teamBudgetDepositCents,
  teamBudgetFeeCents,
} from '@sportninja/common/features/registration/teamBudgetPayment/utils/teamBudgetDeposit'
import { pricedCheckoutNote } from '@sportninja/common/features/registration/teamBudgetPayment/utils/pricedCheckoutNote'
import { supportsTeamBudgetCheckout } from '@sportninja/common/features/registration/teamBudgetPayment/utils/teamBudgetSessionPricing'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import CreditCardInput from 'src/components/CreditCardInput'
import {
  paragraphMedium,
  paragraphSmall,
  weightMedium,
  weightSemiBold,
} from 'src/components/css'
import Icon from 'src/components/Icon'
import { EventCard } from 'src/components/RegistrationEventCard'
import { EventProgress } from 'src/components/RegistrationEventProgress'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import usePaymentProvider, {
  PAYMENT_PROVIDERS,
  PAYMENT_STATUS,
} from 'src/hooks/usePaymentProvider'
import useRegistrationPaymentWebSocket, {
  CONFIRMATION_ERROR_TYPES,
  CONFIRMATION_STATE,
} from 'src/hooks/useRegistrationPaymentWebSocket'
import { registerError } from 'src/utils/sentry'
import LoggedUserBar from '../components/LoggedUserBar'
import PaymentMethodSelector from '../components/PaymentMethodSelector'
import PaymentPlanSelector from '../components/PaymentPlanSelector'
import TeamBudgetPaymentChoice from '../components/TeamBudgetPaymentChoice'
import { useStartOver } from '../hooks/useStartOver'
import { useTeamBudgetPaymentChoice } from '../hooks/useTeamBudgetPaymentChoice'
import { registrationStepPath } from '../registrationSteps'
import { RegistrationContext } from '../context/RegistrationContext'
import { usePaymentGate } from '../hooks/usePaymentGate'
import {
  PAYMENT_PLANS,
  getPaymentPlanOptions,
  isNativeOneTimePayment,
} from '../utils/paymentPlan'
import {
  isDeferredPaymentOffered,
  resolvePaymentSubmitRoute,
} from '../utils/paymentSubmitRoute'
import {
  resolvePaymentScreenHeading,
  resolveZeroDueSummary,
} from '../utils/reviewSummary'
import {
  PageTitle,
  REGISTRATION_STEPS,
  alternateButtonStyle,
  headingButtonStyle,
  headingSixStyle,
  paragraphMediumBoldStyle,
  paragraphMediumStyle,
  paragraphSmallBoldStyle,
} from './Main'

/**
 * Money column treatment. `tabular-nums` gives every digit the same advance
 * width so decimal points line up down the receipt; without it the amounts
 * read as a ragged column.
 */
const tabularAmountStyle = css`
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
`

/**
 * Receipt line item (label and amount). Weight 500 — heavier than the shared
 * paragraphMediumStyle used elsewhere in the flow, which is regular.
 */
const receiptLineStyle = css`
  ${paragraphMedium}
  ${weightMedium}
  color: ${colors.WHITE};
`

/**
 * Secondary receipt line (fee rows and Tax) — 14px against receiptLineStyle's
 * 16px. These are adjustments to the Subtotal rather than items in their own
 * right, so they step down a size; color and weight stay identical so the
 * step is the only distinction and emphasis still belongs to the Total.
 */
const receiptSecondaryLineStyle = css`
  ${paragraphSmall}
  ${weightMedium}
  color: ${colors.WHITE};
`

/** Muted subtitle under a line item, e.g. the competition name. */
const receiptSubtitleStyle = css`
  ${paragraphSmall}
  ${weightSemiBold}
  color: ${colors.NEUTRAL_50};
`

/**
 * RegistrationPayment - Handles the payment step of registration flow.
 * Renders payment info summary and supports Stripe, SportsPay, or in-person payment methods.
 * Uses WebSocket for real-time Stripe payment confirmation.
 * Side effects: API calls for payment processing, navigation to review step on success.
 */
const RegistrationPayment = () => {
  const {
    session,
    setStep,
    goBack,
    setSession,
    registration,
    deleteSession,
    currencyCode,
    couponInfo,
    division,
    role,
    teamOfficialType,
    isTeamRep,
    updateSessionConfirmSubmission,
    updateSessionConfirmSportsPaySubmission,
    updateSessionPayment,
    countries,
    email,
    resetSession,
  } = useContext(RegistrationContext)

  const [
    submittingAlreadyInPersonPayment,
    setSubmittingAlreadyInPersonPayment,
  ] = useState(false)

  const ACTIVE_PAYMENT_PROVIDER =
    registration?.registration?.payment_provider_type?.id

  const waiverRequired = registration?.registration?.show_waiver

  // The registration id comes from the route (the context's `id` is never set).
  const { id: registrationId } = useParams()
  const history = useHistory()

  /*
   * Sep 24 (Jeff + James) — on a Team Budget division the registering rep may pay
   * the deposit (the Minimum To Qualify) here — or any amount up to the whole team
   * fee (Inon, Sep 24) — or carry on without paying. Everything else on this step is
   * unchanged.
   */
  const isTeamBudgetRep = goesToTeamBudgetPaymentStep({ isTeamRep, division })

  /*
   * A registration that is already confirmed — Back after a completed payment, a
   * second tab — is never offered for payment or confirmation again (the server would
   * refuse it): its confirmation step says what is true. Judged once, on arrival.
   */
  const [arrivedConfirmed] = useState(() =>
    isConfirmedRegistrationSession(session)
  )
  useEffect(() => {
    if (arrivedConfirmed) {
      history.replace(
        registrationStepPath(registrationId, REGISTRATION_STEPS.REGISTER_REVIEW)
      )
    }
  }, [arrivedConfirmed, history, registrationId])

  const teamBudgetDeposit = isTeamRep ? teamBudgetDepositCents(division) : null
  const teamBudgetFee = isTeamRep ? teamBudgetFeeCents(division) : null
  const offersRepPayment = offersTeamBudgetPayment({
    isTeamRep,
    division,
    hasPaymentProvider: Boolean(ACTIVE_PAYMENT_PROVIDER),
  })
  /*
   * SN-8441 / SN-8442 — the rep's payment is taken BEFORE the team is registered.
   * Their choice is recorded on the server, which prices it and opens its checkout;
   * the card form is offered only once the session is priced for exactly the choice
   * on screen, and the team registers once the payment goes through. An api2 without
   * the checkout offers Not now only — never the old register-first path.
   */
  const offersTeamBudgetCheckout =
    offersRepPayment && supportsTeamBudgetCheckout(session)
  const repChoice = useTeamBudgetPaymentChoice({
    session,
    enabled: offersTeamBudgetCheckout,
    depositCents: teamBudgetDeposit,
    updateSessionPayment,
  })
  const isRepPaying = offersTeamBudgetCheckout && repChoice.choice !== 'later'
  const repCheckout = session?.team_budget_checkout ?? null
  const formatCents = (cents) => formatCurrency(cents / 100, currencyCode)
  const { startOver } = useStartOver({ resetSession })

  // Local state for error handling
  const [localError, setLocalError] = useState(null)
  const [alreadyPaidInPerson, setAlreadyPaidInPerson] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState(null)
  const [hasSubmittedPayment, setHasSubmittedPayment] = useState(false)
  const [isInSuccessDelay, setIsInSuccessDelay] = useState(false)

  // State for credit card input values (for SportsPay)
  const [creditCardValues, setCreditCardValues] = useState({
    cardNumber: '',
    expiryDate: '',
    cvc: '',
    country: '',
    cardType: '',
    errors: {},
  })

  // Ref to track if element has been mounted
  const elementMounted = useRef(false)

  const {
    total,
    tax,
    subtotal,
    discount,
    client_secret,
    // This is used by sports pay to dinamically load the script
    host_name,
    root_schedule_name,
    connected_account_id,
  } = session

  // Registrant-facing fee rows (League/Payment Processing Fee). Already
  // filtered server-side for this registrant — rendered verbatim.
  const feeLineItems = getFeeLineItems(session)
  const { taxedFees, postTaxFees } = splitFeeLineItemsForReceipt(feeLineItems)

  /**
   * Re-validates the registration session against our API immediately before
   * a payment is handed to the provider. A tab left open past the registration
   * close would otherwise confirm the charge at Stripe/SportsPay first and only
   * then discover the session is dead — money moved, nobody registered.
   */
  const { verifyBeforePayment, isVerifying: isVerifyingSession } =
    usePaymentGate({ sessionId: session?.id })

  // WebSocket hook for real-time payment confirmation (Stripe only)
  const {
    confirmationState,
    confirmedSession,
    errorMessage: wsErrorMessage,
    startWaitingForConfirmation,
    resetConfirmation,
    isWaiting: isWaitingForWsConfirmation,
    isPaymentReceived: isWsPaymentReceived,
    errorType: wsErrorType,
  } = useRegistrationPaymentWebSocket(session?.id)

  /*
   * SN-8441 — the payment went through but the registration is still being completed
   * (or support must apply it): say so, and never offer the card form again.
   */
  const paymentReceivedMessage = isWsPaymentReceived
    ? wsErrorType === CONFIRMATION_ERROR_TYPES.PAYMENT_NEEDS_REVIEW
      ? TEAM_BUDGET_PAYMENT_COPY.needsReview
      : TEAM_BUDGET_PAYMENT_COPY.captured
    : null

  /**
   * Whether the division natively requires a single full payment (true) or uses
   * a deposit-first / installment flow (false). Shares the backend rule
   * (`(installments ?? 1) === 1 && single_payment`, player_* fields for players)
   * via the paymentPlan helper — single_payment alone is NOT enough: with
   * installments > 1 the division still prices a deposit flow.
   */
  const isSinglePayment = useMemo(
    () => isNativeOneTimePayment({ isTeamRep, division }),
    [isTeamRep, division]
  )

  /**
   * The deposit amount relevant to the current registrant type.
   * Team reps use `division.deposit`; players use `division.player_deposit`.
   */
  const effectiveDeposit = useMemo(() => {
    return isTeamRep ? division?.deposit : division?.player_deposit
  }, [isTeamRep, division?.deposit, division?.player_deposit])

  const deposit = useMemo(() => {
    if (couponInfo?.id) {
      return formatCurrency(effectiveDeposit, currencyCode)
    }
    return formatCurrency(subtotal, currencyCode)
  }, [couponInfo?.id, subtotal, currencyCode, effectiveDeposit])

  /**
   * Deposit Only vs Pay in Full. Options are null on native one-time divisions
   * (where the plan does not apply) — the server ignores the plan there too.
   * The server echo (session.payment_plan) is the source of truth for selection.
   */
  const paymentPlanOptions = useMemo(
    () => getPaymentPlanOptions({ isTeamRep, division }),
    [isTeamRep, division]
  )
  const selectedPaymentPlan =
    session?.payment_plan || PAYMENT_PLANS.DEPOSIT_ONLY
  const isPayInFullSelected =
    !!paymentPlanOptions && selectedPaymentPlan === PAYMENT_PLANS.PAY_IN_FULL
  const [isRepricing, setIsRepricing] = useState(false)

  /**
   * Nothing owed right now (e.g. a 100% coupon on the selected plan): no payment
   * element mounts and submission confirms the registration directly.
   */
  const hasNoBalanceDue =
    total !== null && total !== undefined && Number(total) <= 0

  /*
   * SN-8326 — WHY nothing is due, not just that nothing is due.
   *
   * `hasNoBalanceDue` is true for a 100% coupon AND for a Team-Managed Budget division,
   * and the panel below only ever knew the coupon story. `cost_distribution_enabled` is
   * the discriminator; the team total comes from the DIVISION, never from this session's
   * pricing, which is zero by design on this path.
   */
  const zeroDue = useMemo(
    () =>
      resolveZeroDueSummary({
        costDistributionEnabled: division?.cost_distribution_enabled,
        isPlayerRegistration: !isTeamRep,
        teamCost: division?.team_cost,
      }),
    [division?.cost_distribution_enabled, division?.team_cost, isTeamRep]
  )

  /*
   * SN-8326 (R2-04) — the heading has to agree with the receipt below it. On a
   * Team-Managed Budget division nothing is payable on this screen, so it must not ask
   * for a deposit beside a zero Due Today. Non-TMB wording is unchanged.
   */
  const heading = useMemo(
    () =>
      resolvePaymentScreenHeading({
        costDistributionEnabled: division?.cost_distribution_enabled,
        isPlayerRegistration: !isTeamRep,
        isSinglePayment,
        isPayInFullSelected,
        offersDepositPayment: offersTeamBudgetCheckout,
      }),
    [
      division?.cost_distribution_enabled,
      isTeamRep,
      isSinglePayment,
      isPayInFullSelected,
      offersTeamBudgetCheckout,
    ]
  )

  /**
   * Handles the payment success flow with the following steps:
   * 1. For Stripe: Wait for WebSocket payment-confirmed event
   * 2. For SportsPay: Submit token and confirm via API
   * 3. For in-person: Confirm submission via API
   * @returns {Promise<boolean>} True if payment was successful, false otherwise
   */
  const handlePaymentSuccess = async ({ sportsPayToken }) => {
    try {
      // If this is an in-person payment, skip payment processing
      if (alreadyPaidInPerson) {
        try {
          setSubmittingAlreadyInPersonPayment(true)
          setIsInSuccessDelay(true)
          await updateSessionConfirmSubmission(alreadyPaidInPerson)
          setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
          setSubmittingAlreadyInPersonPayment(false)
          setIsInSuccessDelay(false)
          return true
        } catch (submissionError) {
          setSubmittingAlreadyInPersonPayment(false)
          setPaymentStatus(PAYMENT_STATUS.FAILED)
          setIsInSuccessDelay(false)
          handlePaymentError(submissionError)
          return false
        }
      }

      // If this is SportsPay, use token-based submission
      if (ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY) {
        try {
          setIsInSuccessDelay(true)
          // Only US cards collect a billing ZIP; the field is cleared when the
          // billing country changes, so an empty value here is correct.
          await updateSessionConfirmSportsPaySubmission(
            sportsPayToken,
            creditCardValues.zipCode || undefined,
            creditCardValues.countryCode || undefined
          )
          setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
          setIsInSuccessDelay(false)
          return true
        } catch (submissionError) {
          setPaymentStatus(PAYMENT_STATUS.FAILED)
          setIsInSuccessDelay(false)
          handlePaymentError(submissionError)
          return false
        }
      }

      // For Stripe payments, wait for WebSocket confirmation
      // The backend will send payment-confirmed or confirmation-failed event
      console.log(
        '🔄 New_RegistrationPayment: Stripe payment successful, waiting for WebSocket confirmation'
      )
      setIsInSuccessDelay(true)
      startWaitingForConfirmation()
      return true
    } catch (sessionError) {
      // Handle session errors
      if (sessionError?.status === 410) {
        setLocalError('Session Has Expired. Please Try Again.')
        await new Promise((resolve) => setTimeout(resolve, 1500))
        deleteSession()
        window.location.reload()
      } else {
        const errorMessage = getErrorMessage(sessionError)
        setLocalError(errorMessage)
      }

      setHasSubmittedPayment(false)
      setIsInSuccessDelay(false)

      return false
    }
  }

  // Handle payment errors
  const handlePaymentError = useCallback(
    (err) => {
      console.error('Payment error:', err)

      // Get a user-friendly error message
      let errorMessage = getErrorMessage(err)

      // Handle specific error types
      if (err?.type === 'card_error') {
        // Card errors are user-facing errors that occur during payment processing
        errorMessage =
          err.message ||
          'Your card was declined. Please try another payment method.'
      } else if (err?.type === 'validation_error') {
        // Validation errors occur when the card details are invalid
        errorMessage =
          err.message || 'Please check your card details and try again.'
      } else if (
        err?.type === 'api_error' ||
        err?.type === 'api_connection_error'
      ) {
        // API errors are usually temporary issues with the payment service
        errorMessage =
          'We are experiencing issues with our payment service. Please try again later.'
      } else {
        // For any other errors, provide a generic message
        errorMessage =
          errorMessage ||
          'An error occurred while processing your payment. Please try again.'
      }

      // Set the error message to display to the user
      setLocalError(errorMessage)
      setHasSubmittedPayment(false)
      setIsInSuccessDelay(false)

      // Log the error to Sentry for tracking
      registerError(err, 'Payment processing error', {
        session,
        division,
        couponInfo,
        teamOfficialType,
        role,
      })
    },
    [session, division, couponInfo, teamOfficialType, role]
  )

  // Format credit card data for SportsPay
  const formatSportsPayCardData = useCallback(() => {
    if (!creditCardValues) {
      return null
    }

    // Extract month and year from expiry date (MM / YY format)
    const expiryParts = creditCardValues.expiryDate.split(' / ')
    const month = expiryParts[0]
    const year = expiryParts[1] ? `20${expiryParts[1]}` : ''

    return {
      CARDNUM: creditCardValues.cardNumber.replace(/\s+/g, ''),
      CARDEXPMM: month,
      CARDEXPYYYY: year,
      CARDCVV: creditCardValues.cvc,
    }
  }, [creditCardValues])

  // Initialize payment provider hook
  const {
    paymentStatus,
    setPaymentStatus,
    isLoading,
    error: paymentError,
    processPayment,
    resetPayment,
    mountStripeElement,
    destroyStripeElement,
    validateSportsPayCard,
    isInitialized,
  } = usePaymentProvider({
    // With nothing owed there is no intent/secret — a null provider stops the
    // hook from initializing and erroring on the missing secret. A Team Budget rep's
    // card form waits until the choice is priced (its intent exists).
    provider: offersTeamBudgetCheckout
      ? isRepPaying && repChoice.isPriced
        ? ACTIVE_PAYMENT_PROVIDER
        : null
      : hasNoBalanceDue
      ? null
      : ACTIVE_PAYMENT_PROVIDER,
    clientSecret: client_secret,
    sportsPayApiKey: client_secret,
    connectAccount: connected_account_id,
    sportsPayHost: host_name,
    onPaymentSuccess: handlePaymentSuccess,
    onPaymentError: handlePaymentError,
    sessionData: {
      session,
      division,
      couponInfo,
      teamOfficialType,
      role,
    },
  })

  // Handle WebSocket payment confirmation state changes (Stripe only)
  useEffect(() => {
    if (
      confirmationState === CONFIRMATION_STATE.CONFIRMED &&
      confirmedSession
    ) {
      console.log(
        '✅ New_RegistrationPayment: WebSocket confirmed, updating session and navigating'
      )
      setSession(confirmedSession)
      setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
      setIsInSuccessDelay(false)
    } else if (
      confirmationState === CONFIRMATION_STATE.FAILED ||
      confirmationState === CONFIRMATION_STATE.TIMEOUT
    ) {
      console.log(
        '❌ New_RegistrationPayment: WebSocket confirmation failed:',
        wsErrorMessage
      )
      setPaymentStatus(PAYMENT_STATUS.FAILED)
      setLocalError(
        wsErrorMessage || 'Payment confirmation failed. Please try again.'
      )
      setHasSubmittedPayment(false)
      setIsInSuccessDelay(false)
    }
  }, [
    confirmationState,
    confirmedSession,
    wsErrorMessage,
    setSession,
    setStep,
    setPaymentStatus,
  ])

  // Deferral is never offered to a Team Budget rep (paying now or Not now instead)
  const isDeferredPaymentEnabled = isDeferredPaymentOffered({
    isTeamRep,
    isTeamBudgetRep,
    division,
  })

  // Set initial payment method based on deferred payment availability
  useEffect(() => {
    // Only set initial payment method if it's not already set
    if (paymentMethod === null && !isDeferredPaymentEnabled) {
      setPaymentMethod('credit-card')
    }
  }, [isDeferredPaymentEnabled, paymentMethod])

  // Sync alreadyPaidInPerson with paymentMethod
  useEffect(() => {
    setAlreadyPaidInPerson(paymentMethod === 'paid-in-person')
  }, [paymentMethod])

  // Set error if payment information is missing
  useEffect(() => {
    // No balance due (e.g. zero-total plan/coupon combination): the secret is
    // intentionally empty — not an error. A Team Budget rep's card form shows only
    // once the choice is priced, which brings the secret with it.
    if (hasNoBalanceDue || offersTeamBudgetCheckout) {
      return
    }
    if (
      ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE &&
      (!client_secret || !connected_account_id)
    ) {
      setLocalError(
        !client_secret
          ? 'Payment information is missing. Please try again or contact support.'
          : 'Payment processing account is missing. Please try again or contact support.'
      )
    }
  }, [
    client_secret,
    connected_account_id,
    hasNoBalanceDue,
    offersTeamBudgetCheckout,
    ACTIVE_PAYMENT_PROVIDER,
  ])

  /*
   * SN-8441 (P5) — a Team Budget rep's re-priced choice has a new intent: its card
   * form REPLACES the previous one (mountStripeElement destroys it first). With no
   * priced payment on screen (Not now, a choice being priced) no card form remains.
   */
  useEffect(() => {
    if (!offersTeamBudgetCheckout) {
      return
    }
    elementMounted.current = false
    if (!isRepPaying || !repChoice.isPriced) {
      destroyStripeElement()
    }
  }, [
    offersTeamBudgetCheckout,
    client_secret,
    isRepPaying,
    repChoice.isPriced,
    destroyStripeElement,
  ])

  // Mount Stripe element when form is ready
  useEffect(() => {
    // Only mount the element if using Stripe
    if (ACTIVE_PAYMENT_PROVIDER !== PAYMENT_PROVIDERS.STRIPE) {
      return
    }

    // Only mount when credit card is selected
    if (paymentMethod !== 'credit-card') {
      return
    }

    // Only mount the element once
    if (elementMounted.current) {
      return
    }
    if (!isInitialized) {
      return
    }
    const paymentDiv = document.querySelector('#payment-element')
    if (paymentDiv && client_secret && connected_account_id) {
      mountStripeElement('payment-element')
      elementMounted.current = true
    }
  }, [
    client_secret,
    connected_account_id,
    mountStripeElement,
    isInitialized,
    paymentMethod,
    ACTIVE_PAYMENT_PROVIDER,
  ])

  // Validate SportsPay card data when it changes
  useEffect(() => {
    if (
      ACTIVE_PAYMENT_PROVIDER !== PAYMENT_PROVIDERS.SPORTS_PAY ||
      !validateSportsPayCard ||
      !isInitialized
    ) {
      return
    }

    // If there are errors, don't validate
    if (
      creditCardValues?.errors?.cardNumber ||
      creditCardValues?.errors?.expiryDate ||
      creditCardValues?.errors?.cvc
    ) {
      return
    }

    // Only validate if we have enough data
    if (
      creditCardValues.cardNumber &&
      creditCardValues.expiryDate &&
      creditCardValues.cvc &&
      creditCardValues.cvc.length > 3
    ) {
      const cardData = formatSportsPayCardData()
      if (cardData) {
        const validationResult = validateSportsPayCard(cardData)
        if (!validationResult.datavalid) {
          if (!validationResult.cardnum.isvalid) {
            if (validationResult.cardnum.message) {
              setLocalError(validationResult.cardnum.message)
            }
          }
          if (!validationResult.cardexp.isvalid) {
            if (validationResult.cardexp.message) {
              setLocalError(validationResult.cardexp.message)
            }
          }
          if (!validationResult.cardcvv.isvalid) {
            if (validationResult.cardcvv.message) {
              setLocalError(validationResult.cardcvv.message)
            }
          }
          return
        }
      }
    }
  }, [
    creditCardValues,
    validateSportsPayCard,
    isInitialized,
    formatSportsPayCardData,
    ACTIVE_PAYMENT_PROVIDER,
  ])

  /**
   * Blocks a payment that can no longer legitimately go through, and tears the
   * flow down when the failure is terminal.
   * @returns {Promise<boolean>} True when the payment may proceed.
   */
  const passesSessionGate = async () => {
    const gate = await verifyBeforePayment()
    if (gate.ok) {
      return true
    }

    setLocalError(gate.message)
    setHasSubmittedPayment(false)

    // A session the server no longer recognizes leaves nothing to retry
    // against — surface the reason, then restart the flow. Other rejections
    // (validation, transient) keep the session so the visitor can try again.
    if (gate.shouldResetSession) {
      await new Promise((resolve) => setTimeout(resolve, 2500))
      await deleteSession()
      window.location.reload()
    }

    return false
  }

  // Handle form submission (the buttons; Enter goes through handleFormSubmit)
  const handleSubmit = async (e) => {
    e.preventDefault()
    // Clear any previous errors before processing payment
    setLocalError(null)
    setHasSubmittedPayment(true)

    const route = resolvePaymentSubmitRoute({
      submittedBy: 'button',
      offersTeamBudgetCheckout,
      repChoice: repChoice.choice,
      isPriced: repChoice.isPriced,
      isLocked: repChoice.isLocked,
      hasNoBalanceDue,
      paymentMethod,
    })

    if (route === 'blocked') {
      // Validate that a payment method is selected (a Team Budget rep's buttons stay
      // disabled until their choice is priced)
      if (!offersTeamBudgetCheckout) {
        setLocalError('Please select a payment method')
      }
      setHasSubmittedPayment(false)
      return
    }

    // Nothing owed (or a Team Budget rep's Not now): bypass payment-method
    // selection, card collection and the deferred path entirely — confirm straight
    // to the no-payment-due state
    if (route === 'zero_balance' || route === 'team_budget_register') {
      try {
        setIsInSuccessDelay(true)
        await updateSessionConfirmSubmission()
        setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
        setIsInSuccessDelay(false)
      } catch (submissionError) {
        setIsInSuccessDelay(false)
        setHasSubmittedPayment(false)
        handlePaymentError(submissionError)
      }
      return
    }

    // If already paid in person, skip payment processing
    if (route === 'in_person') {
      await handlePaymentSuccess({})
      return
    }

    if (ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY) {
      // Check for credit card validation errors
      if (creditCardValues.errors?.expiryDate) {
        setLocalError(creditCardValues.errors.expiryDate)
        return
      }

      // Billing ZIP is mandatory for US cards
      if (creditCardValues.errors?.zipCode) {
        setLocalError(creditCardValues.errors.zipCode)
        return
      }

      // Format card data for SportsPay
      const cardData = formatSportsPayCardData()
      if (!cardData) {
        setLocalError('Please enter valid card details')
        return
      }

      await processPayment(cardData)
    } else {
      // Stripe payment processing
      await processPayment()
    }
  }

  /**
   * The form's own submit — Enter in a field. A Team Budget rep's Enter records the
   * amount typed and never pays; a card's Enter takes PAY NOW's gated path; the
   * other methods do not pay a provider, so they submit directly.
   */
  const handleFormSubmit = async (e) => {
    const route = resolvePaymentSubmitRoute({
      submittedBy: 'enter',
      offersTeamBudgetCheckout,
      repChoice: repChoice.choice,
      isPriced: repChoice.isPriced,
      isLocked: repChoice.isLocked,
      hasNoBalanceDue,
      paymentMethod,
    })

    if (
      route === 'team_budget_commit' ||
      (route === 'blocked' && offersTeamBudgetCheckout)
    ) {
      e.preventDefault()
      repChoice.commitTyped()
      return
    }

    await (route === 'pay_now' ? handlePayNow(e) : handleSubmit(e))
  }

  /**
   * PAY NOW submission. Unlike the other submit buttons this one hands the
   * registrant's card to a payment provider, so it re-validates the session
   * first and only submits if the server still accepts it.
   *
   * Both Stripe and SportsPay are confirmed straight from the browser, which
   * makes this the last point at which a registration that closed while the
   * tab sat idle can be stopped without the registrant already being charged.
   */
  const handlePayNow = async (e) => {
    e.preventDefault()
    setLocalError(null)
    setHasSubmittedPayment(true)

    if (!(await passesSessionGate())) {
      return
    }

    await handleSubmit(e)
  }

  // Handle credit card input changes
  const handleCreditCardChange = useCallback((values) => {
    setCreditCardValues(values)
  }, [])

  // Reset payment provider and elements (used when unchecking paid in person checkbox)
  const handlePaymentReset = () => {
    resetPayment()
    resetConfirmation()
    // Reset the element mounted flag so it can be mounted again
    elementMounted.current = false
    // Clear any error messages
    setLocalError(null)

    // Force remount of Stripe element by adding a small delay
    if (ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE) {
      setTimeout(() => {
        const paymentDiv = document.querySelector('#payment-element')
        if (
          paymentDiv &&
          client_secret &&
          connected_account_id &&
          isInitialized
        ) {
          mountStripeElement('payment-element')
          elementMounted.current = true
        }
      }, 100)
    }
  }

  /**
   * Switches the payment plan: the server re-prices the session and issues a
   * fresh payment intent, so the mounted payment element must be torn down and
   * remounted against the new client_secret (the mount effect re-runs on it).
   */
  const handlePaymentPlanChange = async (plan) => {
    if (plan === selectedPaymentPlan || isRepricing) {
      return
    }
    setIsRepricing(true)
    setLocalError(null)
    try {
      await updateSessionPayment({ payment_plan: plan })
      resetPayment()
      resetConfirmation()
      elementMounted.current = false
    } catch (err) {
      handlePaymentError(err)
    } finally {
      setIsRepricing(false)
    }
  }

  // Shared back navigation: players with a required waiver step back to the
  // waiver, everyone else steps back to team registration.
  const handleBack = useCallback(() => {
    if (waiverRequired && !isTeamRep) {
      goBack(REGISTRATION_STEPS.WAIVER)
    } else {
      goBack(REGISTRATION_STEPS.TEAM_REGISTER)
    }
  }, [waiverRequired, isTeamRep, goBack])

  // A US billing ZIP is mandatory; block PAY NOW until it is present and valid
  const isBillingZipMissing =
    ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY &&
    !alreadyPaidInPerson &&
    !!creditCardValues.errors?.zipCode

  // Determine which error to display (local or from payment provider)
  const displayError = localError || paymentError

  // Combine all relevant loading/processing states to prevent session loss actions
  const isPaymentProcessing = useMemo(() => {
    return (
      hasSubmittedPayment &&
      (paymentStatus === PAYMENT_STATUS.PROCESSING ||
        isInSuccessDelay ||
        submittingAlreadyInPersonPayment ||
        isWaitingForWsConfirmation)
    )
  }, [
    hasSubmittedPayment,
    paymentStatus,
    isInSuccessDelay,
    submittingAlreadyInPersonPayment,
    isWaitingForWsConfirmation,
  ])

  // Warn user when attempting to close/refresh tab during payment processing
  useEffect(() => {
    if (!isPaymentProcessing) {
      return
    }

    const beforeUnloadHandler = (e) => {
      // Modern browsers ignore custom text; setting returnValue triggers the prompt.
      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', beforeUnloadHandler)
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [isPaymentProcessing])

  // UI fragments for each payment method. Plain derivations on purpose: they
  // capture handleSubmit, whose identity changes every render, so a useMemo
  // here would recompute every render anyway (and trips exhaustive-deps).
  const creditCardUI =
    displayError ||
    paymentStatus === PAYMENT_STATUS.SUCCESS ||
    paymentStatus === PAYMENT_STATUS.FAILED ||
    !ACTIVE_PAYMENT_PROVIDER ? null : (
      <>
        {ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY && (
          <CreditCardInput
            onChange={handleCreditCardChange}
            countries={countries}
            disabled={alreadyPaidInPerson}
          />
        )}
        {ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE && (
          <div id='payment-element' />
        )}
        <div
          css={css`
            display: flex;
            gap: 16px;
            width: 100%;
            margin-top: 16px;
          `}
        >
          <Button
            onClick={handleBack}
            text='BACK'
            variant='secondary'
            disabled={isLoading || isRepricing}
          />
          <Button
            onClick={handlePayNow}
            text='PAY NOW'
            variant='primary'
            disabled={
              isLoading ||
              isRepricing ||
              isVerifyingSession ||
              isBillingZipMissing
            }
            isLoading={isLoading || isVerifyingSession}
          />
        </div>
      </>
    )

  /**
   * SN-8441 / SN-8442 — the Team Budget rep's panel: the choice (the deposit, another
   * amount, or Not now), recorded and priced on the server as it changes; the card
   * form once the choice is priced; PAY NOW (the team registers once the payment goes
   * through) or COMPLETE REGISTRATION for Not now — both disabled until priced. A
   * payment already taken locks it all: never pay again.
   */
  const repRefusal = repChoice.isLocked ? null : repChoice.refusal
  const teamBudgetCheckoutUI =
    displayError ||
    paymentStatus === PAYMENT_STATUS.SUCCESS ||
    paymentStatus === PAYMENT_STATUS.FAILED ? null : (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          width: 100%;
        `}
      >
        <TeamBudgetPaymentChoice
          choice={repChoice.choice}
          onChoiceChange={repChoice.setChoice}
          customAmount={repChoice.customAmount}
          onCustomAmountChange={repChoice.commitCustomAmount}
          onCustomAmountTyped={repChoice.onCustomAmountTyped}
          amountError={repChoice.fieldError}
          depositCents={teamBudgetDeposit ?? 0}
          teamFeeCents={teamBudgetFee ?? 0}
          formatCents={formatCents}
          country={registration?.registration?.currency_country}
          disabled={repChoice.isLocked || isPaymentProcessing || isLoading}
        />
        {repChoice.isLocked ? (
          <Alert
            type='info'
            showIcon
            message={capitalizeWords(TEAM_BUDGET_PAYMENT_COPY.lockedTitle)}
            description={TEAM_BUDGET_PAYMENT_COPY.locked}
            data-testid='team-budget-payment-locked'
          />
        ) : null}
        {repRefusal ? (
          <Alert
            type='warning'
            showIcon
            message={repRefusal.message}
            data-testid='team-budget-payment-refusal'
            action={
              repRefusal.kind === 'outdated' ? (
                <Button
                  onClick={() => window.location.reload()}
                  text={TEAM_BUDGET_PAYMENT_COPY.reload}
                  variant='secondary'
                />
              ) : (
                <Button
                  onClick={repChoice.retry}
                  text={TEAM_BUDGET_PAYMENT_COPY.tryAgain}
                  variant='secondary'
                  disabled={repChoice.isCommitting}
                />
              )
            }
          />
        ) : null}
        {isRepPaying && repChoice.isPriced ? (
          <>
            {ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY && (
              <CreditCardInput
                onChange={handleCreditCardChange}
                countries={countries}
              />
            )}
            {ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE && (
              <div id='payment-element' />
            )}
          </>
        ) : null}
        {isRepPaying &&
        !repChoice.isPriced &&
        !repChoice.isLocked &&
        !repChoice.fieldError &&
        !repRefusal ? (
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {TEAM_BUDGET_PAYMENT_COPY.pricing}
          </Text>
        ) : null}
        {/* Buttons never wrap their label: on a phone the pair takes two lines. */}
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
            width: 100%;
          `}
        >
          <Button
            onClick={handleBack}
            text='BACK'
            variant='secondary'
            disabled={isLoading || isPaymentProcessing}
          />
          {isRepPaying ? (
            <Button
              onClick={handlePayNow}
              text={TEAM_BUDGET_PAYMENT_COPY.payNow}
              variant='primary'
              disabled={
                !repChoice.isPriced ||
                isLoading ||
                isVerifyingSession ||
                isBillingZipMissing ||
                isPaymentProcessing
              }
              isLoading={isLoading || isVerifyingSession}
            />
          ) : (
            <Button
              onClick={handleSubmit}
              text={TEAM_BUDGET_PAYMENT_COPY.submitLater}
              variant='primary'
              disabled={!repChoice.isPriced || isInSuccessDelay}
              isLoading={isInSuccessDelay || repChoice.isCommitting}
            />
          )}
        </div>
      </div>
    )

  /**
   * Rendered INSTEAD of the payment-method selector when nothing is owed
   * (e.g. a 100% coupon on the selected plan): no method choice, no card form,
   * no deferred path — submission confirms the registration directly.
   */
  const zeroBalanceUI =
    displayError ||
    paymentStatus === PAYMENT_STATUS.SUCCESS ||
    paymentStatus === PAYMENT_STATUS.FAILED ? null : (
      <div
        css={css`
          display: flex;
          padding: 16px;
          background: ${colors.NEUTRAL_600};
          border-radius: 8px;
          border: 1px solid ${colors.NEUTRAL_400};
          flex-direction: column;
          gap: 16px;
          width: 100%;
        `}
      >
        <Text variant='paragraphMedium' color={colors.WHITE} weight='bold'>
          Payment
        </Text>
        <div
          css={css`
            align-self: stretch;
            height: 0;
            border: 0.5px solid ${colors.NEUTRAL_400};
          `}
        />
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          {zeroDue.message}
        </Text>
        {zeroDue.note ? (
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {zeroDue.note}
          </Text>
        ) : null}
        {/* An api2 that can't take the payment before registering: Not now only. */}
        {offersRepPayment && !offersTeamBudgetCheckout ? (
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {TEAM_BUDGET_PAYMENT_COPY.payUnavailable}
          </Text>
        ) : null}
        <div
          css={css`
            display: flex;
            gap: 16px;
            width: 100%;
          `}
        >
          <Button
            onClick={handleBack}
            text='BACK'
            variant='secondary'
            disabled={isRepricing || isInSuccessDelay}
          />
          <Button
            onClick={handleSubmit}
            text='COMPLETE REGISTRATION'
            variant='primary'
            disabled={isRepricing || isInSuccessDelay}
            isLoading={isInSuccessDelay}
          />
        </div>
      </div>
    )

  const paidInPersonUI =
    displayError ||
    paymentStatus === PAYMENT_STATUS.SUCCESS ||
    paymentStatus === PAYMENT_STATUS.FAILED ? null : (
      <>
        <Button
          onClick={handleBack}
          text='BACK'
          variant='secondary'
          disabled={isLoading || submittingAlreadyInPersonPayment}
        />
        <Button
          onClick={handleSubmit}
          text='SUBMIT REGISTRATION'
          variant='primary'
          disabled={isLoading || submittingAlreadyInPersonPayment}
          isLoading={isLoading || submittingAlreadyInPersonPayment}
        />
      </>
    )

  // Leaving for the confirmation step (see arrivedConfirmed).
  if (arrivedConfirmed) {
    return null
  }

  return (
    <>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          justify-content: center;
          background-color: ${colors.NEUTRAL_800};
          width: 1080px;
          padding: 0 35px 35px 35px;
          border-radius: 16px 16px 0px 0px;
          @media (max-width: 768px) {
            min-width: 0;
            width: 100%;
          }
        `}
      >
        <LoggedUserBar
          email={email}
          disabled={isPaymentProcessing}
          onStartOver={startOver}
        />
        <EventCard eventInfo={registration} />
        <EventProgress
          eventProgress={5}
          isWaiverRequired={waiverRequired}
          isTeamRep={isTeamRep}
        />
        <PageTitle
          title={heading.title}
          subtitle={heading.subtitle}
          showAlert={true}
          showSuccess={false}
        />
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            align-self: stretch;
            @media (max-width: 768px) {
              flex-direction: column;
              gap: 0px;
              align-items: center;
              max-height: unset;
            }
            ${ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY &&
            `
              max-height: unset;
            `}
          `}
        >
          {/* Payment Info - SportNinja Data */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              /* Tight row rhythm so the line items read as one receipt block.
                 The dividers and the deposit block carry their own margins, so
                 this only affects the spacing between amount rows. */
              gap: 2px;
              flex: 1 0 0;
              @media (max-width: 768px) {
                margin-bottom: 16px;
                width: 100%;
              }
              background-color: ${colors.NEUTRAL_600};
              border-radius: 8px;
              border: 1px solid ${colors.NEUTRAL_400};
              padding: 16px;
            `}
          >
            <p
              css={css`
                ${paragraphMediumBoldStyle}
                color: ${colors.WHITE};
                align-self: stretch;
              `}
            >
              Payment Info
            </p>
            <div
              css={css`
                align-self: stretch;
                height: 0;
                margin: 4px 0 8px;
                border: 0.5px solid ${colors.NEUTRAL_400};
              `}
            />
            {isPayInFullSelected && paymentPlanOptions ? (
              // Pay in Full breakdown (Figma 19521:12341): Deposit Fee + the
              // remainder labelled "Registration Fee"; together they sum to Subtotal
              <>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    align-self: stretch;
                  `}
                >
                  <p
                    css={css`
                      ${receiptLineStyle}
                      flex: 1 0 0;
                    `}
                  >
                    Deposit Fee
                  </p>
                  <p
                    css={css`
                      ${receiptLineStyle}
                      ${tabularAmountStyle}
                    `}
                  >
                    {formatCurrency(
                      paymentPlanOptions.depositAmount,
                      currencyCode
                    )}
                  </p>
                </div>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    align-self: stretch;
                    /* Matches the deposit-only branch: separates the purchased
                       items from the totals rows below. */
                    margin-bottom: 20px;
                  `}
                >
                  <p
                    css={css`
                      ${receiptLineStyle}
                      flex: 1 0 0;
                    `}
                  >
                    Registration Fee
                  </p>
                  <p
                    css={css`
                      ${receiptLineStyle}
                      ${tabularAmountStyle}
                    `}
                  >
                    {formatCurrency(
                      paymentPlanOptions.fullAmount -
                        paymentPlanOptions.depositAmount,
                      currencyCode
                    )}
                  </p>
                </div>
              </>
            ) : isTeamBudgetRep ? (
              /* Sep 24 — a Team Budget rep's deposit is the Minimum To Qualify, never
                 the division's legacy deposit (which read "Deposit Fee $0.00" here). */
              teamBudgetDeposit !== null ? (
                <div
                  css={css`
                    display: flex;
                    align-items: flex-start;
                    gap: 16px;
                    align-self: stretch;
                    margin-bottom: 20px;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      justify-content: center;
                      align-items: flex-start;
                      flex: 1 0 0;
                    `}
                  >
                    <p css={receiptLineStyle}>
                      {TEAM_BUDGET_PAYMENT_COPY.depositRowLabel}
                    </p>
                    <p css={receiptSubtitleStyle}>{root_schedule_name}</p>
                  </div>
                  <p
                    css={css`
                      ${receiptLineStyle}
                      ${tabularAmountStyle}
                    `}
                  >
                    {formatCents(teamBudgetDeposit)}
                  </p>
                </div>
              ) : null
            ) : effectiveDeposit && effectiveDeposit > 0 ? (
              <div
                css={css`
                  display: flex;
                  /* flex-start, not center: the label is a two-line stack
                     (Deposit Fee / competition name) and the amount must sit on
                     the same baseline as the first line, not float between. */
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                  /* Separates the purchased item from the totals rows below. */
                  margin-bottom: 20px;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: flex-start;
                    flex: 1 0 0;
                  `}
                >
                  <p css={receiptLineStyle}>Deposit Fee</p>
                  <p css={receiptSubtitleStyle}>{root_schedule_name}</p>
                </div>
                <p
                  css={css`
                    ${receiptLineStyle}
                    ${tabularAmountStyle}
                  `}
                >
                  {deposit}
                </p>
              </div>
            ) : null}
            {/* Separator */}
            <div
              css={css`
                width: 100%;
                border-color: ${colors.VERY_DARK_GRAYISH_BLUE};
                border-bottom-width: 1px;
                border-bottom-style: solid;
              `}
            />
            {/* SN-8326 (R2-04): a Team-Managed Budget session prices at zero, so a
                "Subtotal $0.00 / Tax $0.00" breakdown above a $5,000 team total says
                nothing and re-creates the "is this free?" reading the ticket is about.
                The team total and Due Today below carry the whole story. */}
            {zeroDue.teamTotal === null ? (
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${receiptLineStyle}
                    flex: 1 0 0;
                  `}
                >
                  Subtotal
                </p>
                <p
                  css={css`
                    ${receiptLineStyle}
                    ${tabularAmountStyle}
                  `}
                >
                  {formatCurrency(subtotal, currencyCode)}
                </p>
              </div>
            ) : null}
            {/* Coupon discount sits directly under Subtotal (it's an adjustment
                to the Subtotal), before the League/processing fee lines. A Team
                Budget rep's figures take no coupon, so none of these rows show for
                them (round 3: a deposit coupon read "-$50.00" above the full amount). */}
            {!isTeamBudgetRep &&
            couponInfo?.id &&
            isSinglePayment &&
            couponInfo?.is_installment ? (
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    color: ${colors.SUCCESS_100};
                    flex: 1 0 0;
                  `}
                >
                  Coupon Code: {couponInfo?.code}
                </p>
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    ${tabularAmountStyle}
                    color: ${colors.SUCCESS_100};
                  `}
                >
                  -{formatCurrency(couponInfo?.discount_amount, currencyCode)}
                </p>
              </div>
            ) : null}
            {!isTeamBudgetRep &&
            couponInfo?.id &&
            isPayInFullSelected &&
            discount > 0 ? (
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    color: ${colors.SUCCESS_100};
                    flex: 1 0 0;
                  `}
                >
                  Coupon Code: {couponInfo?.code}
                </p>
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    ${tabularAmountStyle}
                    color: ${colors.SUCCESS_100};
                  `}
                >
                  -{formatCurrency(discount, currencyCode)}
                </p>
              </div>
            ) : null}
            {!isTeamBudgetRep &&
            couponInfo?.id &&
            !isSinglePayment &&
            !isPayInFullSelected &&
            couponInfo?.is_deposit ? (
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    color: ${colors.SUCCESS_100};
                    flex: 1 0 0;
                  `}
                >
                  Coupon Code: {couponInfo?.code}
                </p>
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    ${tabularAmountStyle}
                    color: ${colors.SUCCESS_100};
                  `}
                >
                  -
                  {formatCurrency(
                    couponInfo?.deposit_discount_amount,
                    currencyCode
                  )}
                </p>
              </div>
            ) : null}
            {/* League fees sit above Tax (taxed); payment processing sits below
                Tax (struck on the final amount). Keyed off fee identity, not
                API order — see splitFeeLineItemsForReceipt. */}
            {taxedFees.map((fee) => (
              <div
                key={fee.key}
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    flex: 1 0 0;
                  `}
                >
                  {fee.label}
                </p>
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    ${tabularAmountStyle}
                  `}
                >
                  {formatCurrency(fee.amount, currencyCode)}
                </p>
              </div>
            ))}
            {zeroDue.teamTotal === null ? (
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    flex: 1 0 0;
                  `}
                >
                  Tax
                </p>
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    ${tabularAmountStyle}
                  `}
                >
                  {formatCurrency(tax, currencyCode)}
                </p>{' '}
              </div>
            ) : null}
            {postTaxFees.map((fee) => (
              <div
                key={fee.key}
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    flex: 1 0 0;
                  `}
                >
                  {fee.label}
                </p>
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    ${tabularAmountStyle}
                  `}
                >
                  {formatCurrency(fee.amount, currencyCode)}
                </p>
              </div>
            ))}

            {/* Separator */}
            <div
              css={css`
                align-self: stretch;
                height: 0;
                margin: 8px 0 4px;
                border: 0.5px solid ${colors.NEUTRAL_400};
              `}
            />
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 22px;
                align-self: stretch;
              `}
            >
              <p
                css={css`
                  ${headingButtonStyle}
                  color: ${colors.WHITE};
                  flex: 1 0 0;
                `}
              >
                {zeroDue.teamTotal !== null
                  ? 'Team Total (before tax)'
                  : 'Total'}
              </p>
              <p
                css={css`
                  ${headingButtonStyle}
                  color: ${colors.WHITE};
                  ${tabularAmountStyle}
                `}
              >
                {formatCurrency(
                  zeroDue.teamTotal !== null ? zeroDue.teamTotal : total,
                  currencyCode
                )}
              </p>
            </div>
            {/* SN-8326 — a Team-Managed Budget receipt prices this session at zero because
                the fee is collected from the players later. R2-04: the division's real team
                cost IS the total above, so the only figure left to state here is that
                nothing is collected today, and on what basis players are billed. Two
                competing totals on one screen was the original defect. */}
            {zeroDue.teamTotal !== null ? (
              <>
                <div
                  css={css`
                    display: flex;
                    align-items: flex-start;
                    gap: 16px;
                    align-self: stretch;
                  `}
                >
                  <p
                    css={css`
                      ${receiptLineStyle}
                      flex: 1 0 0;
                    `}
                  >
                    Due Today
                  </p>
                  <p
                    css={css`
                      ${receiptLineStyle}
                      ${tabularAmountStyle}
                    `}
                    data-testid='payment-due-today'
                  >
                    {/* SN-8441 — exactly what the rep's priced choice charges
                        (tax included, in the currency it is charged in); $0.00
                        for Not now; a dash while it is being priced. */}
                    {isRepPaying
                      ? repChoice.isPriced && repCheckout
                        ? formatCurrency(
                            repCheckout.total,
                            repCheckout.currency
                          )
                        : '—'
                      : formatCurrency(total, currencyCode)}
                  </p>
                </div>
                <p
                  css={css`
                    ${receiptSecondaryLineStyle}
                    align-self: stretch;
                  `}
                >
                  {isRepPaying
                    ? repChoice.isPriced && repCheckout
                      ? pricedCheckoutNote({
                          tax: repCheckout.tax,
                          formatAmount: (amount) =>
                            formatCurrency(amount, repCheckout.currency),
                        })
                      : repChoice.isCommitting
                      ? TEAM_BUDGET_PAYMENT_COPY.pricing
                      : TEAM_BUDGET_PAYMENT_COPY.registeredOncePaid
                    : zeroDue.receiptNote}
                </p>
              </>
            ) : null}
            {isSinglePayment || isPayInFullSelected ? null : (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 4px;
                  align-self: stretch;
                  /* display none if couponInfo is null */
                  display: ${!isTeamBudgetRep &&
                  couponInfo?.id &&
                  !couponInfo?.is_deposit
                    ? 'flex'
                    : 'none'};
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: flex-start;
                    gap: 16px;
                    align-self: stretch;
                  `}
                >
                  <p
                    css={css`
                      ${paragraphMediumStyle}
                      ${weightSemiBold}
                      color: ${colors.SUCCESS_100};
                      flex: 1 0 0;
                    `}
                  >
                    Coupon Code: {couponInfo?.code}
                  </p>
                  <p
                    css={css`
                      ${paragraphMediumStyle}
                      ${weightSemiBold}
                      color: ${colors.SUCCESS_100};
                    `}
                  >
                    -{formatCurrency(couponInfo?.discount_amount, currencyCode)}
                  </p>
                </div>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  * Discount will be applied to the remaining balance.
                </Text>
              </div>
            )}
          </div>
          {/* Separator Between SportNinja and Stripe Stuff */}
          <div
            css={css`
              height: 100%;
              width: 1px;
              background-color: ${colors.VERY_DARK_GRAYISH_BLUE};
            `}
          />
          {/* Payment Provider UI */}
          <div
            css={css`
              width: 50%;
              display: flex;
              align-items: center;
              justify-content: flex-start;
              flex-direction: column;
              @media (max-width: 768px) {
                width: 100%;
                padding-top: 0px;
              }
            `}
          >
            <div
              css={css`
                width: 100%;
              `}
            >
              {paymentStatus === PAYMENT_STATUS.SUCCESS && !displayError ? (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 250px;
                  `}
                >
                  <Icon name='check' color={colors.PRIMARY} fontSize={24} />
                  <p
                    css={css`
                      ${headingButtonStyle}
                      color: ${colors.WHITE};
                      margin-top: 32px;
                      text-align: center;
                    `}
                  >
                    {isWaitingForWsConfirmation
                      ? 'Payment Successful.\nConfirming your registration...'
                      : 'Payment Successful.\nYou will be redirected shortly.'}
                  </p>
                  <Spin
                    css={css`
                      margin-top: 32px;
                      color: ${colors.PRIMARY};
                    `}
                  />
                </div>
              ) : paymentStatus === PAYMENT_STATUS.FAILED || displayError ? (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 300px;
                    width: 100%;
                    overflow: hidden;
                  `}
                >
                  <Icon
                    name={paymentReceivedMessage ? 'clock' : 'times'}
                    color={
                      paymentReceivedMessage
                        ? colors.PRIMARY
                        : colors.ERROR_LIGHT
                    }
                    fontSize={24}
                  />
                  <p
                    css={css`
                      ${headingButtonStyle}
                      color: ${colors.WHITE};
                      margin-top: 32px;
                      text-align: center;
                      width: 100%;
                      overflow-wrap: break-word;
                      word-wrap: break-word;
                      word-break: break-word;
                    `}
                  >
                    {paymentReceivedMessage
                      ? capitalizeWords(TEAM_BUDGET_PAYMENT_COPY.capturedTitle)
                      : 'Payment Failed'}
                  </p>
                  <span
                    css={css`
                      ${paragraphSmallBoldStyle}
                      color: ${colors.WHITE};
                      margin-top: 16px;
                      margin-bottom: 16px;
                      text-align: center;
                      max-width: 90%;
                      overflow-wrap: break-word;
                      word-wrap: break-word;
                      word-break: break-word;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    `}
                  >
                    {paymentReceivedMessage ||
                      displayError ||
                      'Something went wrong'}
                  </span>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: row;
                      justify-content: center;
                      align-items: center;
                      gap: 16px;
                      margin-top: 16px;
                      @media (max-width: 768px) {
                        flex-direction: column-reverse;
                        width: 100%;
                        gap: 8px;
                      }
                    `}
                  >
                    <button
                      type='button'
                      hidden={Boolean(paymentReceivedMessage)}
                      disabled={isLoading || Boolean(paymentReceivedMessage)}
                      onClick={() => {
                        resetPayment()
                        resetConfirmation()
                        elementMounted.current = false
                        setLocalError(null)

                        // SN-8441 (review R2) — a declined SportsPay card ends its
                        // checkout: the same choice is priced again (a new checkout,
                        // a fresh card setup) before the card form comes back. The
                        // server keeps a payment that may have been taken, and says so.
                        if (
                          isRepPaying &&
                          ACTIVE_PAYMENT_PROVIDER ===
                            PAYMENT_PROVIDERS.SPORTS_PAY
                        ) {
                          repChoice.retry()
                        }

                        if (
                          ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE
                        ) {
                          setTimeout(() => {
                            const paymentDiv =
                              document.querySelector('#payment-element')
                            if (
                              paymentDiv &&
                              client_secret &&
                              connected_account_id &&
                              isInitialized
                            ) {
                              mountStripeElement('payment-element')
                              elementMounted.current = true
                            }
                          }, 100)
                        }
                      }}
                      css={css`
                        ${alternateButtonStyle}
                        ${headingSixStyle}
                        width: 100%;
                        max-height: 40px;
                        cursor: ${isLoading ? 'not-allowed' : 'pointer'};
                      `}
                    >
                      BACK
                    </button>
                  </div>
                </div>
              ) : (
                <form id='payment-form' onSubmit={handleFormSubmit}>
                  {paymentPlanOptions ? (
                    <PaymentPlanSelector
                      options={paymentPlanOptions}
                      selectedPlan={selectedPaymentPlan}
                      onChange={handlePaymentPlanChange}
                      disabled={
                        isRepricing ||
                        isLoading ||
                        isPaymentProcessing ||
                        submittingAlreadyInPersonPayment
                      }
                      formatAmount={(amount) =>
                        formatCurrency(amount, currencyCode)
                      }
                    />
                  ) : null}
                  {offersTeamBudgetCheckout ? (
                    teamBudgetCheckoutUI
                  ) : hasNoBalanceDue ? (
                    zeroBalanceUI
                  ) : (
                    <PaymentMethodSelector
                      isDeferredPaymentEnabled={isDeferredPaymentEnabled}
                      selectedMethod={paymentMethod}
                      onChange={(method) => {
                        if (
                          paymentMethod === 'paid-in-person' &&
                          method === 'credit-card'
                        ) {
                          handlePaymentReset()
                        }
                        // Reset element mounted flag when changing payment methods
                        if (paymentMethod !== method) {
                          elementMounted.current = false
                        }
                        setPaymentMethod(method)
                      }}
                      disabled={isLoading || submittingAlreadyInPersonPayment}
                      isPaymentProviderNotSet={!ACTIVE_PAYMENT_PROVIDER}
                      onBack={handleBack}
                      backDisabled={
                        isLoading || submittingAlreadyInPersonPayment
                      }
                    >
                      {paymentMethod === 'credit-card'
                        ? creditCardUI
                        : paymentMethod === 'paid-in-person'
                        ? paidInPersonUI
                        : null}
                    </PaymentMethodSelector>
                  )}
                  {/* Show back button when no payment method is selected and defer payment is enabled */}
                  {!offersTeamBudgetCheckout &&
                    !hasNoBalanceDue &&
                    paymentMethod === null &&
                    isDeferredPaymentEnabled && (
                      <div
                        css={css`
                          display: flex;
                          justify-content: flex-end;
                          width: 100%;
                          margin-top: 16px;
                        `}
                      >
                        <Button
                          onClick={handleBack}
                          text='BACK'
                          variant='secondary'
                          disabled={
                            isLoading || submittingAlreadyInPersonPayment
                          }
                          css={css`
                            width: 50%;
                            max-width: 200px;
                            @media (max-width: 768px) {
                              width: 100%;
                              max-width: 100%;
                            }
                          `}
                        />
                      </div>
                    )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default RegistrationPayment
